import React from 'react';
import { Icon, Avatar, Badge, Button } from '../components/UI';
import { useApp } from '../context/AppContext';

function exportPDF(data, students) {
  const totalCourses = data.programmes.reduce((a, p) => a + p.courses.length, 0);
  const totalEnrolments = students.reduce((a, s) => a + s.programmes.length, 0);

  const progRows = data.programmes.map(prog => {
    const enrolled = students.filter(s => s.programmes.includes(prog.id)).length;
    const ext = prog.courses.reduce((a, c) => a + (c.studentCount || 0), 0);
    const pct = students.length > 0 ? Math.round((enrolled / students.length) * 100) : 0;
    const links = prog.courses.filter(c => c.link).length;
    return `<tr>
      <td>${prog.name}</td>
      <td>${prog.courses.length}</td>
      <td>${enrolled} (${pct}%)</td>
      <td>${ext || '—'}</td>
      <td>${links}/${prog.courses.length}</td>
    </tr>`;
  }).join('');

  const studentRows = students.map(s => {
    const progs = s.programmes.map(pid => data.programmes.find(p => p.id === pid)?.name).filter(Boolean).join(', ') || 'None';
    const courses = s.programmes.reduce((a, pid) => a + (data.programmes.find(p => p.id === pid)?.courses.length || 0), 0);
    return `<tr><td>${s.name}</td><td>${s.email}</td><td>${progs}</td><td>${courses}</td></tr>`;
  }).join('');

  const date = new Date().toLocaleDateString('en-ZA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>AlgoAtWork — Stats Report</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:-apple-system,system-ui,sans-serif;padding:40px;color:#2D3035;line-height:1.5}
    .brand{display:inline-block;background:#EC7D2A;color:white;font-size:11px;font-weight:700;padding:3px 12px;border-radius:99px;letter-spacing:.08em;text-transform:uppercase;margin-bottom:12px}
    h1{font-size:26px;font-weight:800;letter-spacing:-.02em;margin-bottom:4px}
    .sub{font-size:14px;color:#6B7079;margin-bottom:32px;padding-bottom:20px;border-bottom:2px solid #EC7D2A}
    .grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:36px}
    .card{background:#F4F5F6;border-radius:12px;padding:16px 18px}
    .card .val{font-size:30px;font-weight:800;color:#EC7D2A;line-height:1}
    .card .lbl{font-size:12px;color:#6B7079;margin-top:3px}
    h2{font-size:17px;font-weight:700;margin-bottom:12px;color:#2D3035}
    table{width:100%;border-collapse:collapse;margin-bottom:36px;font-size:13px}
    th{text-align:left;padding:9px 13px;background:#F4F5F6;font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#6B7079;font-weight:700}
    td{padding:11px 13px;border-bottom:1px solid #E9EAEC;color:#2D3035}
    tr:last-child td{border-bottom:none}
    .footer{font-size:11px;color:#9CA0A8;text-align:center;padding-top:16px;border-top:1px solid #E9EAEC}
    @media print{body{padding:20px}}
  </style>
</head>
<body>
  <div class="brand">AlgoAtWork</div>
  <h1>Programme Stats Report</h1>
  <p class="sub">Generated on ${date}</p>

  <div class="grid">
    <div class="card"><div class="val">${data.programmes.length}</div><div class="lbl">Programmes</div></div>
    <div class="card"><div class="val">${students.length}</div><div class="lbl">Students</div></div>
    <div class="card"><div class="val">${totalCourses}</div><div class="lbl">Courses</div></div>
    <div class="card"><div class="val">${totalEnrolments}</div><div class="lbl">Enrolments</div></div>
  </div>

  <h2>Programme Breakdown</h2>
  <table>
    <thead><tr><th>Programme</th><th>Courses</th><th>Hub students</th><th>External students</th><th>Live links</th></tr></thead>
    <tbody>${progRows}</tbody>
  </table>

  <h2>Student Breakdown</h2>
  <table>
    <thead><tr><th>Name</th><th>Email</th><th>Programmes</th><th>Courses access</th></tr></thead>
    <tbody>${studentRows}</tbody>
  </table>

  <p class="footer">AlgoAtWork · ${new Date().toISOString()}</p>
</body>
</html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 400);
}

export default function AdminStats() {
  const { data } = useApp();
  const students = data.students.filter(s => s.role === 'student');

  return (
    <div className="page-enter" style={{ maxWidth: 1280, margin: '0 auto', padding: '44px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 36 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 30, fontWeight: 800, color: 'var(--dark)', margin: '0 0 6px', letterSpacing: '-0.02em' }}>Programme Stats</h1>
          <p style={{ fontSize: 14, color: 'var(--dark-60)', margin: 0 }}>Overview of enrolments and course coverage across all programmes.</p>
        </div>
        <Button variant="ghost" onClick={() => exportPDF(data, students)} style={{ flexShrink: 0 }}>
          <Icon name="external" size={15} color="var(--dark-60)" /> Export PDF
        </Button>
      </div>

      {/* Per-programme cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 20, marginBottom: 44 }}>
        {data.programmes.map(prog => {
          const enrolled = students.filter(s => s.programmes.includes(prog.id)).length;
          const extStudents = prog.courses.reduce((a, c) => a + (c.studentCount || 0), 0);
          const withLinks = prog.courses.filter(c => c.link).length;
          return (
            <div key={prog.id} style={{ background: 'var(--white)', border: '1.5px solid var(--dark-10)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
              <div style={{ height: 5, background: prog.color }} />
              <div style={{ padding: 22 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: prog.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name={prog.icon} size={18} color={prog.color} />
                  </div>
                  <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: 'var(--dark)', margin: 0 }}>{prog.name}</h3>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {[
                    { label: 'Hub enrolments', value: enrolled },
                    { label: 'External students', value: extStudents || '—' },
                    { label: 'Courses', value: prog.courses.length },
                    { label: 'Live links', value: `${withLinks}/${prog.courses.length}` },
                  ].map(s => (
                    <div key={s.label} style={{ background: 'var(--dark-05)', borderRadius: 9, padding: '11px 13px' }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: prog.color, lineHeight: 1, fontFamily: 'var(--font-heading)' }}>{s.value}</div>
                      <div style={{ fontSize: 11, color: 'var(--dark-60)', marginTop: 3 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Enrolment bar chart */}
      <div style={{ background: 'var(--white)', border: '1.5px solid var(--dark-10)', borderRadius: 'var(--radius-xl)', overflow: 'hidden', marginBottom: 44 }}>
        <div style={{ padding: '20px 26px', borderBottom: '1px solid var(--dark-10)' }}>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: 'var(--dark)', margin: 0 }}>Student enrolment by programme</h2>
        </div>
        {data.programmes.map((prog, i) => {
          const enrolled = students.filter(s => s.programmes.includes(prog.id)).length;
          const pct = students.length > 0 ? Math.round((enrolled / students.length) * 100) : 0;
          return (
            <div key={prog.id} style={{ padding: '16px 26px', borderBottom: i < data.programmes.length - 1 ? '1px solid var(--dark-10)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon name={prog.icon} size={15} color={prog.color} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--dark)', fontFamily: 'var(--font-heading)' }}>{prog.name}</span>
                </div>
                <span style={{ fontSize: 13, color: 'var(--dark-60)' }}>{enrolled} / {students.length} students ({pct}%)</span>
              </div>
              <div style={{ height: 7, background: 'var(--dark-10)', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: prog.color, borderRadius: 99, transition: 'width 0.7s ease' }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Student breakdown table */}
      <div style={{ background: 'var(--white)', border: '1.5px solid var(--dark-10)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
        <div style={{ padding: '20px 26px', borderBottom: '1px solid var(--dark-10)' }}>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: 'var(--dark)', margin: 0 }}>Student breakdown</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr 1fr', padding: '11px 22px', background: 'var(--dark-05)', borderBottom: '1px solid var(--dark-10)' }}>
          {['Name', 'Programmes', 'Courses access'].map(h => (
            <div key={h} style={{ fontSize: 11, fontWeight: 700, color: 'var(--dark-60)', letterSpacing: '0.07em', textTransform: 'uppercase', fontFamily: 'var(--font-heading)' }}>{h}</div>
          ))}
        </div>
        {students.map((student, i) => {
          const totalCourses = student.programmes.reduce((a, pid) => {
            const prog = data.programmes.find(p => p.id === pid);
            return a + (prog?.courses.length || 0);
          }, 0);
          return (
            <div key={student.id} style={{ display: 'grid', gridTemplateColumns: '2fr 3fr 1fr', padding: '14px 22px', borderBottom: i < students.length - 1 ? '1px solid var(--dark-10)' : 'none', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Avatar name={student.name} size={32} />
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--dark)', fontFamily: 'var(--font-heading)' }}>{student.name}</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {student.programmes.length === 0
                  ? <span style={{ fontSize: 12, color: 'var(--dark-40)' }}>None</span>
                  : student.programmes.map(pid => {
                      const prog = data.programmes.find(p => p.id === pid);
                      return prog ? <Badge key={pid} bg={prog.color + '18'} color={prog.color}>{prog.name}</Badge> : null;
                    })}
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--dark)', fontFamily: 'var(--font-heading)' }}>{totalCourses}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
